import { CodeBlock, CopyBlock, monokai } from "react-code-blocks";
import { Link, useOutletContext } from "react-router-dom";
import {
    closeStream,
    closeStreamResponse,
    startIntradayStream,
    startIntradayStreamUpdateMessage,
    streamingURL
} from "../Assets/apiDefinitionExamples";
import { PageNames, TabNames } from "../Assets/variables";
import { useEffect } from "react";

const APIDefinitionStreamingIntraday = () => {
    const {setActiveTabItem} = useOutletContext();

    useEffect(() => {
        setActiveTabItem(TabNames.APIDefinition);
    }, []);

    return (
        <div id="tab-definition" class="api-panel">
            <div className="api-definition-container">
                <p>Streaming Intraday Trades with History API will provide streaming access to real time trades synchronized with current day's historical trades.</p>
                <p>You need a web socket client to receive the streaming data.</p>
                <p>Details on getting started with a streaming client can be found in our <Link to={`/${PageNames.Documentation}#web-socket-client-example`}>documentation</Link>.</p>

                <span className="heading-2">Streaming URL</span>
                <p>Below is the web socket URL for the streaming market data API</p>

                <div className="code-example">
                    <CopyBlock
                        text={streamingURL}
                        language={"text"}
                        showLineNumbers={false}
                        startingLineNumber={1}
                        theme={monokai}
                        codeBlock={true}
                    />
                </div>

                <span className="heading-2">Start Stream</span>
                <p>You can start the stream by sending a <strong>startIntradayStream</strong> message.</p>
                <p>Below is an example of a <strong>startIntradayStream</strong> message that requests trades for ABB</p>

                <div className="code-example">
                    <CopyBlock
                        text={startIntradayStream}
                        language={"text"}
                        showLineNumbers={true}
                        startingLineNumber={1}
                        theme={monokai}
                        codeBlock={true}
                    />
                </div>

                <p>The responses will have a START message followed by HISTORY and UPDATE messages.</p>

                <div className="code-example">
                    <CodeBlock
                        text={startIntradayStreamUpdateMessage}
                        language={"text"}
                        showLineNumbers={true}
                        startingLineNumber={1}
                        theme={monokai}
                        codeBlock={true}
                    />
                </div>

                <span className="heading-3">Start Stream Parameters</span>

                <table>
                    <thead>
                        <tr>
                            <th>Parameter</th>
                            <th>Type</th>
                            <th>Available Values</th>
                            <th>Example</th>
                            <th>Description</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>streamId (query)</td>
                            <td>string</td>
                            <td></td>
                            <td>stream1, NASDAQ_Equities</td>
                            <td>streamId can be provided as one of the input parameters to identify the stream. The streamId can be used to close a specific stream.</td>
                        </tr>
                        <tr>
                            <td>scheme* (query)</td>
                            <td>enum string</td>
                            <td>ISIN_BC, VALOR_BC, SEDOL_BC, CUSIP_BC, TICKER_BC</td>
                            <td>TICKER_BC</td>
                            <td>Listing scheme is a combination of the instrument scheme and the market scheme separated by underscore.</td>
                        </tr>
                        <tr>
                            <td>ids* (query)</td>
                            <td>string</td>
                            <td></td>
                            <td>ABBN_4</td>
                            <td>Listing Identifier to be provided by client as input parameter. You can query one id per stream</td>
                        </tr>
                        <tr>
                            <td>api-version (header)</td>
                            <td>string(date)</td>
                            <td></td>
                            <td>2022-01-01</td>
                            <td>Version of the API as date in ISO 8601 format.</td>
                        </tr>
                        <tr>
                            <td>intradayHistory (query)</td>
                            <td>boolean</td>
                            <td>TRUE, FALSE</td>
                            <td>TRUE</td>
                            <td>Enable intraday trade history for the current day. If it is missing, the flag will be set to 'TRUE' by default.</td>
                        </tr>
                        <tr>
                            <td>qualityOfService (query)</td>
                            <td>enum string</td>
                            <td>REAL_TIME, DELAYED</td>
                            <td>DELAYED</td>
                            <td>The up-to-dateness of price data, also referred to as 'Data Quality Timeliness' or 'License Quality,' is determined by the default priority when no specific quality is chosen by the client in their request. The available quality levels are delivered based on the following order:
                                <ul>
                                    <li>REAL_TIME</li>
                                    <li>DELAYED</li>
                                </ul>
                            </td>
                        </tr>
                    </tbody>
                </table>

                <span className="heading-3">Response Fields</span>
                <p>While creating the query, you can select the fields you would like to see in the response.</p>
                <p>The available price fields are described in the 'Content' tab</p>
                <p>Apart from the price fields the below additional fields can be requested as part of the query.</p>

                <table>
                    <thead>
                        <tr>
                            <th>Field Name</th>
                            <th>Sub Fields</th>
                            <th>Description</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>streamId</td>
                            <td></td>
                            <td>
                                <ul>
                                    <li>In order to stream different fields for different listings, it is possible to "tag" every message with a <strong>streamId</strong>. This is delivered as part of every message.</li>
                                    <li>This enables advanced workflow, like the possibility to start / stop multiple stream within the same WebSocket connection identified by a unique <strong>streamId</strong>.</li>
                                    <li>If the streamId is not unique within the current WebSocket connection, an error is raised.</li>
                                    <li>The stream ID can be defined as an input parameter while composing the query.</li>
                                </ul>
                            </td>
                        </tr>
                        <tr>
                            <td>type</td>
                            <td></td>
                            <td>
                                Type of the message:
                                <ul>
                                    <li><strong>START</strong>: this is delivered only by <strong>startIntradayStream</strong> subscription and it represents the "initial image" of the market stream. All data, including reference data, is delivered in this message.</li>
                                    <li><strong>HISTORY</strong>: this is delivered if <strong>intradayHistory</strong> is set to be <strong>true</strong> in the query. History messages consists of all the trades from the beginning of the day. The <strong>historyComplete</strong> field will be <strong>true</strong> for the last history message.</li>
                                    <li><strong>UPDATE</strong>: this is delivered only by <strong>startIntradayStream</strong> subscription and it represent an update. Reference data is not delivered in this message.</li>
                                    <li><strong>STOP</strong>: this is delivered only by <strong>closeStream</strong> mutation and represents the end message of the stream.</li>
                                    <li><strong>ERROR</strong>: delivered in case it is not possible to resolve the requested listing (e.g. wrong ISIN)</li>
                                </ul>
                            </td>
                        </tr>
                        <tr>
                            <td>requestedId</td>
                            <td></td>
                            <td>Listing Identification provided as input parameter.</td>
                        </tr>
                        <tr>
                            <td>requestedScheme</td>
                            <td></td>
                            <td>Listing Scheme provided as input parameter.</td>
                        </tr>
                        <tr>
                            <td>lookupStatus</td>
                            <td></td>
                            <td>Status of the response.</td>
                        </tr>
                        <tr>
                            <td rowSpan={5}>lookup</td>
                            <td></td>
                            <td>lookup provides key reference data fields about the listing. The below are the fields that are available in lookup</td>
                        </tr>
                        <tr>
                            <td>listingShortName</td>
                            <td>Listing short name with up to 19 characters.</td>
                        </tr>
                        <tr>
                            <td>marketShortName</td>
                            <td>Market short name with up to 19 characters.</td>
                        </tr>
                        <tr>
                            <td>listingCurrency</td>
                            <td>The trading currency, as specified by SIX, typically follows the ISO 4217 alpha-3 code. This is shown in the main currency and not in fractional units.</td>
                        </tr>
                        <tr>
                            <td>listingStatus</td>
                            <td>This shows the instruments status on the market - it shows if it is listed, suspended, admited to trading, delisted, etc.</td>
                        </tr>
                        <tr>
                            <td>historyComplete</td>
                            <td></td>
                            <td>Only provided for messages with type = HISTORY. Is set to <strong>true</strong> is no more HISTORY messages will be delivered.</td>
                        </tr>
                    </tbody>
                </table>

                <span className="heading-2">Close Stream</span>
                <p>In order to close a stream, send the below <strong>closeStream</strong> query as a mutation along with the streamId.</p>

                <div className="code-example">
                    <CopyBlock
                        text={closeStream}
                        language={"text"}
                        showLineNumbers={true}
                        startingLineNumber={1}
                        theme={monokai}
                        codeBlock={true}
                    />
                </div>

                <p>You will receive a STOP message upon closing the stream</p>

                <div className="code-example">
                    <CodeBlock
                        text={closeStreamResponse}
                        language={"text"}
                        showLineNumbers={false}
                        startingLineNumber={1}
                        theme={monokai}
                        codeBlock={true}
                    />
                </div>
            </div>
        </div>
    );
}

export default APIDefinitionStreamingIntraday;
